// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '../../log/log';
import { searchTasks, sortTasks } from './filter';
import {
  clearSelection as clearSelectionPure,
  enterSelection,
  toggleSelection as toggleSelectionPure,
} from './selection';
import type {
  TaskFilter,
  TaskFolderId,
  TaskId,
  TaskItem,
  TaskSelection,
  TaskSort,
  TasksData,
  TasksLoader,
  TasksState,
  TasksSubscriber,
  TasksUpdater,
} from './types';

export interface UseTasksViewModelArgs {
  readonly load: TasksLoader;
  readonly subscribe: TasksSubscriber;
  /** Optional updater for completion toggles / inline edits. */
  readonly update?: TasksUpdater;
  /** Override only for tests. The cross-platform default is 80ms. */
  readonly filterDebounceMs?: number;
  /** Override only for tests. The cross-platform default is 200ms. */
  readonly searchDebounceMs?: number;
}

export interface TasksViewModel {
  readonly state: TasksState;
  readonly filter: TaskFilter;
  readonly query: string;
  readonly sort: TaskSort;
  readonly selection: TaskSelection;
  readonly openFolderIds: ReadonlySet<TaskFolderId>;
  readonly setFilter: (filter: TaskFilter) => void;
  readonly setQuery: (query: string) => void;
  readonly setSort: (sort: TaskSort) => void;
  readonly enterSelectionMode: () => void;
  readonly toggleSelection: (id: TaskId) => void;
  readonly clearSelection: () => void;
  readonly toggleFolderOpen: (id: TaskFolderId) => void;
  readonly toggleCompletion: (task: TaskItem) => void;
  readonly retry: () => void;
}

const DEFAULT_FILTER_DEBOUNCE_MS = 80;
const DEFAULT_SEARCH_DEBOUNCE_MS = 200;

/**
 * Owns the Tasks state machine. Filter changes debounce 80ms; query
 * changes debounce 200ms; sort changes commit immediately. Selection
 * mode is preserved across loader emissions and across filter / query
 * commits — only an explicit `clearSelection()` returns to `inactive`.
 *
 * Task titles are sensitive content (Hard rule 3) — the view-model logs
 * `{ verb, count, filter, sort, queryLen }` shape only.
 */
export function useTasksViewModel(args: UseTasksViewModelArgs): TasksViewModel {
  const { load, subscribe, update } = args;
  const filterDebounceMs = args.filterDebounceMs ?? DEFAULT_FILTER_DEBOUNCE_MS;
  const searchDebounceMs = args.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS;

  const [filter, setFilterRaw] = useState<TaskFilter>('all');
  const [query, setQueryRaw] = useState<string>('');
  const [sort, setSort] = useState<TaskSort>('mostRecent');
  const [selection, setSelection] = useState<TaskSelection>({ mode: 'inactive' });
  const [openFolderIds, setOpenFolderIds] = useState<ReadonlySet<TaskFolderId>>(
    () => new Set<TaskFolderId>(),
  );
  const [state, setState] = useState<TasksState>({ kind: 'loading' });
  const [retryNonce, setRetryNonce] = useState(0);

  // Committed values are what the loader / subscriber actually consume.
  // The raw values update synchronously so the chip / input feel responsive,
  // but commit waits the platform-pinned debounce.
  const [committedFilter, setCommittedFilter] = useState<TaskFilter>('all');
  const [committedQuery, setCommittedQuery] = useState<string>('');

  const filterDebounceHandle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryDebounceHandle = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (filterDebounceHandle.current !== null) {
      clearTimeout(filterDebounceHandle.current);
    }
    filterDebounceHandle.current = setTimeout(() => {
      setCommittedFilter(filter);
    }, filterDebounceMs);
    return () => {
      if (filterDebounceHandle.current !== null) {
        clearTimeout(filterDebounceHandle.current);
      }
    };
  }, [filter, filterDebounceMs]);

  useEffect(() => {
    if (queryDebounceHandle.current !== null) {
      clearTimeout(queryDebounceHandle.current);
    }
    queryDebounceHandle.current = setTimeout(() => {
      setCommittedQuery(query);
    }, searchDebounceMs);
    return () => {
      if (queryDebounceHandle.current !== null) {
        clearTimeout(queryDebounceHandle.current);
      }
    };
  }, [query, searchDebounceMs]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const apply = (data: TasksData): void => {
      if (cancelled) {
        return;
      }
      const filtered = searchTasks(data.tasks, committedQuery);
      const sorted = sortTasks(filtered, sort);
      if (sorted.length === 0 && data.folders.length === 0) {
        setState({ kind: 'empty', filter: committedFilter, query: committedQuery });
        return;
      }
      setState({
        kind: 'populated',
        folders: data.folders,
        tasks: sorted,
        filter: committedFilter,
        query: committedQuery,
        sort,
      });
    };

    setState({ kind: 'loading' });

    // Re-trigger on retry without depending on the nonce value's identity.
    void retryNonce;

    const bootstrap = async (): Promise<void> => {
      try {
        log.debug('tasks.bootstrap', {
          filter: committedFilter,
          sort,
          queryLen: committedQuery.length,
        });
        unsubscribe = subscribe(committedFilter, committedQuery, apply);
        const initial = await load(committedFilter, committedQuery);
        apply(initial);
      } catch (error) {
        if (cancelled) {
          return;
        }
        log.error('tasks.bootstrap.failed', error as Error, {
          filter: committedFilter,
          sort,
          queryLen: committedQuery.length,
        });
        setState({ kind: 'error', error: error as Error });
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      if (unsubscribe !== null) {
        unsubscribe();
      }
    };
  }, [committedFilter, committedQuery, sort, load, subscribe, retryNonce]);

  const setFilter = useCallback((next: TaskFilter) => {
    setFilterRaw(next);
  }, []);

  const setQuery = useCallback((next: string) => {
    setQueryRaw(next);
  }, []);

  const enterSelectionMode = useCallback(() => {
    setSelection(enterSelection());
    log.debug('tasks.selection.enter');
  }, []);

  const toggleSelection = useCallback((id: TaskId) => {
    setSelection((current) => toggleSelectionPure(current, id));
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(clearSelectionPure());
    log.debug('tasks.selection.clear');
  }, []);

  const toggleFolderOpen = useCallback((id: TaskFolderId) => {
    setOpenFolderIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleCompletion = useCallback(
    (task: TaskItem) => {
      const nextStateValue: TasksData['tasks'][number]['state'] =
        task.state === 'completed' ? 'pending' : 'completed';

      // Optimistic update: rewrite the local list so the UI feels instant.
      setState((current): TasksState => {
        if (current.kind !== 'populated') {
          return current;
        }
        const tasks: readonly TaskItem[] = current.tasks.map((t) =>
          t.id === task.id ? { ...t, state: nextStateValue, updatedAt: Date.now() } : t,
        );
        return { ...current, tasks };
      });

      if (update === undefined) {
        return;
      }

      void (async () => {
        try {
          await update(task.id, { state: nextStateValue });
          log.debug('tasks.completion.toggled');
        } catch (error) {
          log.error('tasks.completion.failed', error as Error);
          // Roll back to the original state captured in the closure.
          setState((current): TasksState => {
            if (current.kind !== 'populated') {
              return current;
            }
            const tasks: readonly TaskItem[] = current.tasks.map((t) =>
              t.id === task.id ? { ...t, state: task.state, updatedAt: task.updatedAt } : t,
            );
            return { ...current, tasks };
          });
        }
      })();
    },
    [update],
  );

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  return {
    state,
    filter,
    query,
    sort,
    selection,
    openFolderIds,
    setFilter,
    setQuery,
    setSort,
    enterSelectionMode,
    toggleSelection,
    clearSelection,
    toggleFolderOpen,
    toggleCompletion,
    retry,
  };
}
